import React, { FC } from 'react';
import Box from '@material-ui/core/Box';
import Helmet from 'react-helmet';
import { SearchProvider } from '@elastic/react-search-ui';
import searchConfig from '../config/searchConfig';
import config from '../config';
import { startPostMessageService } from '../lib/postMessageService';
import { postGTMAttributes } from '../lib/gtmService';
import { globalHistory } from '@reach/router';
import { NotificationsContextProvider } from '../providers/NotificationsProvider';
import CssBaseline from '@material-ui/core/CssBaseline';
import TransitionProvider from '../providers/TransitionProvider';
import PromotionProvider from '../providers/PromotionProvider';
import { ThemeProvider } from '../providers/ThemeProvider';
import TranslationLoader from '../components/translation-loader/TranslationLoader';
import ErrorBoundary from '../components/error-boundary/ErrorBoundary';
import Backdrop from '../components/backdrop';
import BroadcastCommunication from '../components/BroadcastCommunication';
import { Navigation } from '../components/navigation/Navigation';
import PgeFooter from '../components/navigation/pge-footer';
import Notifications from '../components/notifications';
import SessionTimeout from '../components/SessionTimeout';
import PathProvider from '../providers/PathProvider';
import ROUTES from '../routes';
import { AccountsContextProvider } from '../providers/AccountsProvider';
import AccountDisconnectedBanner from '../components/account-disconnected-banner/AccountDisconnectedBanner';
import { LanguagePreferenceAlert } from '../components/language-preference-alert/LanguagePreferenceAlert';
import { EVChargerFormStateProvider } from '../providers/EVChargerFormStateProvider';
import { RebateOnlyFormStateProvider } from '../providers/RebateOnlyFormStateProvider';
import { NoAccountsRedirectProvider } from '../providers/NoAccountsRedirectConfigProvider';
import { StormModeInterceptProvider } from '../providers/StormModeInterceptProvider';
import BroadcastOutageCommunication from '../components/outage/outage-center/broadcast-outage-communication/BroadcastOutageCommunication';
import OutageMapNotifications from '../components/outage-map-notifications';
import { OutageMapNotificationContextProvider } from '../providers/OutageMapNotificationProvider';

startPostMessageService();

globalHistory.listen(({ location }) => {
  postGTMAttributes(location.pathname);
});

interface Props {
  readonly element: any;
  readonly props: Readonly<{
    pageContext: Record<string, any>;
    pageResources: Record<string, any>;
    location: Record<string, any>;
  }>;
}

const wrapPageElement: FC<Props> = ({
  element,
  props: { pageContext, pageResources, location },
}) => {
  const isBrowser = !(typeof window === 'undefined');
  const pathname = location.pathname.replace(/\/$/, '');

  const isOutagesMobileApp =
    isBrowser &&
    window.location.pathname.replace(/\/$/, '') === ROUTES.OUTAGES_MOBILE_APP;
  const isPGEPLUSGetStarted =
    isBrowser &&
    window.location.pathname.replace(/\/$/, '') === ROUTES.PGE_PLUS_GET_STARTED;

  const isPGEServiceAreaMap =
    isBrowser && pathname === ROUTES.PGE_SERVICE_AREA_MAP;
  const isOutagesPage = isBrowser && pathname === ROUTES.OUTAGES;
  const isCepIrpFeedbackForm =
    isBrowser && pathname === ROUTES.CEP_IRP_STAKEHOLDER_FEEDBACK;
  const isPublicEnergyConsultationForm =
    isBrowser && pathname === ROUTES.PUBLIC_ENERGY_CONSULTATION_FORM;

  const hideHeaderAndNav =
    isOutagesMobileApp ||
    isPGEPLUSGetStarted ||
    isPGEServiceAreaMap ||
    isCepIrpFeedbackForm ||
    isPublicEnergyConsultationForm;
  const hideFooter =
    isPGEServiceAreaMap ||
    isCepIrpFeedbackForm ||
    isPublicEnergyConsultationForm;
  // Must not depend on isBrowser — Helmet scripts in static HTML execute before hydration can suppress them
  const hideScripts =
    pathname === ROUTES.PGE_SERVICE_AREA_MAP ||
    pathname === ROUTES.CEP_IRP_STAKEHOLDER_FEEDBACK ||
    pathname === ROUTES.PUBLIC_ENERGY_CONSULTATION_FORM ||
    pathname === ROUTES.SURVEY;
  // The survey URL carries a single-use token. useSurvey strips it from the address bar,
  // but APM's page-load transaction can be recorded first, so the RUM agent stays off there.
  const hideApm = pathname === ROUTES.SURVEY;

  const renderPageComponents = () => (
    <Box
      id={'full-height-shim'}
      display={'flex'}
      flexDirection={'column'}
      minHeight="100vh"
    >
      {!hideHeaderAndNav && (
        <>
          {!isOutagesPage && (
            <Box id="global-messages-container">
              <BroadcastCommunication />
            </Box>
          )}
          <Box id="pge-header">
            <Navigation />
          </Box>
        </>
      )}
      <Box id="global-notifications-banner">
        {!isPGEServiceAreaMap && <LanguagePreferenceAlert />}
        {isOutagesPage && (
          <>
            <Box id="global-messages-container">
              <BroadcastCommunication />
            </Box>
            <Box id="outage-global-messages-container">
              <BroadcastOutageCommunication />
            </Box>
            <Box id="outage-notification-messages-container">
              <OutageMapNotifications />
            </Box>
          </>
        )}
        <Notifications />
        <AccountDisconnectedBanner />
      </Box>
      <Box flexGrow={1}>{element}</Box>
      <Backdrop />
      <Box flexShrink={0}>{!hideFooter && <PgeFooter />}</Box>
      <Helmet>
        <meta name="google" content="notranslate" />
        {process.env.GATSBY_ACTIVE_ENV === 'production' && !hideApm && (
          <script>{`
          ;(function(d, s, c) {
              var j = d.createElement(s),
              t = d.getElementsByTagName(s)[0]
              j.src = '/static/scripts/elastic-apm-rum.umd.min.js'
              j.onload = function() {window.elasticApm && window.elasticApm.init(c)}
              t.parentNode.insertBefore(j, t)
            })(document, 'script', {
              serviceName: 'cs_client_${config.env}',
              environment:'${config.env}',
              serverUrl: '${config.apm.serverUrl}',
              active: ${config.apm.active},
            })
          `}</script>
        )}
        {!hideScripts && (
          <script src="https://nebula-cdn.kampyle.com/wu/646316/onsite/embed.js" />
        )}
        {process.env.GATSBY_ACTIVE_ENV === 'production' && !hideScripts && (
          <script
            id="cookieyes"
            type="text/javascript"
            src="https://cdn-cookieyes.com/client_data/1b8f42625d28da851cda436264700064/script.js"
          ></script>
        )}
        {process.env.GATSBY_ACTIVE_ENV === 'test' && !hideScripts && (
          <script
            id="cookieyes"
            type="text/javascript"
            src="https://cdn-cookieyes.com/client_data/4dc94c6e6c3fd0dd66749d09d43766d7/script.js"
          ></script>
        )}
      </Helmet>
    </Box>
  );

  return (
    <ThemeProvider>
      <TransitionProvider>
        <NotificationsContextProvider>
          <StormModeInterceptProvider>
            <OutageMapNotificationContextProvider>
              <AccountsContextProvider>
                <PromotionProvider
                  context={{ ...pageContext, ...pageResources }}
                >
                  <EVChargerFormStateProvider>
                    <RebateOnlyFormStateProvider>
                      <NoAccountsRedirectProvider>
                        <PathProvider context={pageContext.slugPathMap}>
                          <CssBaseline />
                          <TranslationLoader />
                          <ErrorBoundary>
                            <SessionTimeout />
                            {/* <StormInfoMessage path={pageResources?.page?.path} /> */}
                            {isBrowser && (
                              <SearchProvider config={searchConfig}>
                                {renderPageComponents()}
                              </SearchProvider>
                            )}
                            {!isBrowser && renderPageComponents()}
                          </ErrorBoundary>
                        </PathProvider>
                      </NoAccountsRedirectProvider>
                    </RebateOnlyFormStateProvider>
                  </EVChargerFormStateProvider>
                </PromotionProvider>
              </AccountsContextProvider>
            </OutageMapNotificationContextProvider>
          </StormModeInterceptProvider>
        </NotificationsContextProvider>
      </TransitionProvider>
    </ThemeProvider>
  );
};

export default wrapPageElement;
